import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Lucide from '@/components/Base/Lucide';
import Button from '@/components/Base/Button';
import { getApiOrigin } from '@/config/env';
import { getToken } from '@/utils/auth';

type AvailableEmail = { fm: string; email: string };

type Settings = {
  enabled: boolean;
  time: string;
  selectedEmails: 'all' | string[];
};

type ApiSettings = {
  settings: Settings;
  availableEmails: AvailableEmail[];
};

export default function OosScheduler() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [time, setTime] = useState('08:00');
  const [availableEmails, setAvailableEmails] = useState<AvailableEmail[]>([]);
  const [selectedEmails, setSelectedEmails] = useState<'all' | string[]>('all');

  const origin = getApiOrigin();
  const headers = { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' };

  // ── Load settings ──
  useEffect(() => {
    setLoading(true);
    fetch(`${origin}/api/v1/admin/oos-scheduler/settings`, { headers })
      .then((r) => r.json())
      .then((json: ApiSettings & { ok?: boolean; error?: string }) => {
        if (!json.ok) throw new Error(json.error ?? 'Failed to load');
        setEnabled(json.settings.enabled);
        setTime(json.settings.time);
        setSelectedEmails(json.settings.selectedEmails);
        setAvailableEmails(json.availableEmails);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load settings'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [recipientSearch, setRecipientSearch] = useState('');

  const filteredAvailableEmails = availableEmails.filter(
    (item) =>
      item.fm.toLowerCase().includes(recipientSearch.toLowerCase()) ||
      item.email.toLowerCase().includes(recipientSearch.toLowerCase())
  );

  const isAllSelected  = selectedEmails === 'all' ||
    (Array.isArray(selectedEmails) && selectedEmails.length === availableEmails.length && availableEmails.length > 0);
  const isNoneSelected = Array.isArray(selectedEmails) && selectedEmails.length === 0;
  const isSomeSelected = !isAllSelected && !isNoneSelected;

  const selectAllRef = (el: HTMLInputElement | null) => {
    if (el) el.indeterminate = isSomeSelected;
  };

  const toggleSelectAll = () => {
    // If all selected → deselect all; otherwise → select all
    setSelectedEmails(isAllSelected ? [] : 'all');
  };

  const toggleEmail = (email: string) => {
    if (selectedEmails === 'all') {
      setSelectedEmails(availableEmails.map((e) => e.email).filter((e) => e !== email));
      return;
    }
    const next = selectedEmails.includes(email)
      ? selectedEmails.filter((e) => e !== email)
      : [...selectedEmails, email];
    setSelectedEmails(next.length === availableEmails.length ? 'all' : next);
  };

  const isEmailChecked = (email: string) =>
    selectedEmails === 'all' || (selectedEmails as string[]).includes(email);

  const selectedCount = selectedEmails === 'all'
    ? availableEmails.length
    : (selectedEmails as string[]).length;

  // ── Save settings ──
  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const res = await fetch(`${origin}/api/v1/admin/oos-scheduler/settings`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ enabled, time, selectedEmails }),
      });
      const json = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !json.ok) throw new Error(json.error ?? `HTTP ${res.status}`);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ── Send now ──
  const handleSendNow = async () => {
    if (!window.confirm("This will send today's OOS emails immediately. Continue?")) return;
    setSendingNow(true);
    setError(null);
    try {
      const res = await fetch(`${origin}/api/v1/admin/oos-scheduler/send-now`, {
        method: 'POST',
        headers,
      });
      const json = (await res.json()) as { ok?: boolean; error?: string; message?: string };
      if (!res.ok || !json.ok) throw new Error(json.error ?? `HTTP ${res.status}`);
      alert(json.message ?? 'OOS emails are being sent in the background.');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to trigger send');
    } finally {
      setSendingNow(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl">

      {/* ── Header ── */}
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => navigate('/oos-email-report')}
          className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-darkmode-400 text-slate-500"
        >
          <Lucide icon="ArrowLeft" className="w-4 h-4 stroke-[2]" />
        </button>
        <div>
          <p className="text-sm text-white">
            Automatically send daily OOS reports to Field Managers at a set time (Malaysia Time).
          </p>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-danger/10 text-danger text-sm border border-danger/20">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-10">
          <Lucide icon="Loader" className="w-4 h-4 animate-spin" /> Loading settings…
        </div>
      ) : (
        <div className="space-y-6">

          {/* ── Card: Schedule ── */}
          <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
              <Lucide icon="Clock" className="w-4 h-4 text-primary" />
              Schedule
            </h3>

            {/* Enable toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Enable scheduler</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  When enabled, emails are sent automatically each day at the time below.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEnabled((v) => !v)}
                className={[
                  'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200',
                  enabled ? 'bg-primary' : 'bg-slate-200 dark:bg-darkmode-400',
                ].join(' ')}
              >
                <span
                  className={[
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200',
                    enabled ? 'translate-x-5' : 'translate-x-0',
                  ].join(' ')}
                />
              </button>
            </div>

            {/* Time */}
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200 w-28 flex-shrink-0">
                Send time (MYT)
              </label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="rounded-md border border-slate-200 dark:border-darkmode-400 bg-white dark:bg-darkmode-700 px-3 py-1.5 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <span className="text-xs text-slate-400">Malaysia Time (UTC+8)</span>
            </div>
          </div>

          {/* ── Card: Email recipients ── */}
          <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 p-5">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2 mb-3">
              <Lucide icon="Users" className="w-4 h-4 text-primary" />
              Recipients
              <span className="ml-auto text-xs font-normal text-slate-400">
                {selectedCount} of {availableEmails.length} selected
              </span>
            </h3>

            {availableEmails.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">
                No FM email addresses found in the Outlet table.
              </p>
            ) : (
              <div className="space-y-1">
                {/* Search box */}
                <div className="relative mb-2">
                  <Lucide icon="Search" className="absolute inset-y-0 left-2.5 my-auto w-3.5 h-3.5 text-slate-400 stroke-[1.5]" />
                  <input
                    type="text"
                    value={recipientSearch}
                    onChange={(e) => setRecipientSearch(e.target.value)}
                    placeholder="Search FM or email…"
                    className="w-full rounded-md border border-slate-200 dark:border-darkmode-400 bg-white dark:bg-darkmode-700 pl-8 pr-3 py-1.5 text-xs text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  {recipientSearch && (
                    <button
                      type="button"
                      onClick={() => setRecipientSearch('')}
                      className="absolute inset-y-0 right-2 my-auto text-slate-400 hover:text-slate-600"
                    >
                      <Lucide icon="X" className="w-3 h-3" />
                    </button>
                  )}
                </div>

                {/* Select all row */}
                <label className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-600 border border-transparent hover:border-slate-200 dark:hover:border-darkmode-400 transition-colors">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded accent-primary cursor-pointer"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                      {isAllSelected ? 'Deselect All' : 'Select All'}
                    </p>
                    <p className="text-xs text-slate-400">
                      {isAllSelected
                        ? 'Click to deselect all Field Managers'
                        : 'Click to select all Field Managers'}
                    </p>
                  </div>
                </label>

                <div className="border-t border-slate-100 dark:border-darkmode-400 my-2" />

                {/* Individual FM rows */}
                <div className="max-h-64 overflow-y-auto space-y-0.5 pr-1">
                  {filteredAvailableEmails.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-4">No results for "{recipientSearch}"</p>
                  )}
                  {filteredAvailableEmails.map((item) => (
                    <label
                      key={item.email}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-600 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={isEmailChecked(item.email)}
                        onChange={() => toggleEmail(item.email)}
                        className="w-4 h-4 rounded accent-primary cursor-pointer flex-shrink-0"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{item.fm}</p>
                        <p className="text-xs text-slate-400 truncate">{item.email}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Actions ── */}
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving}
              className="min-w-[120px]"
            >
              {saving ? (
                <><Lucide icon="Loader" className="w-4 h-4 mr-2 animate-spin stroke-[1.3]" />Saving…</>
              ) : saved ? (
                <><Lucide icon="Check" className="w-4 h-4 mr-2 stroke-[1.3]" />Saved</>
              ) : (
                <><Lucide icon="Save" className="w-4 h-4 mr-2 stroke-[1.3]" />Save Settings</>
              )}
            </Button>

          </div>

        </div>
      )}
    </div>
  );
}
